/**
 * H1CandidatePicker — choose an existing on-page heading to become the H1.
 *
 * The generated suggestion invents wording. When the page already shows a
 * perfectly good title as an <h2>, inserting a NEW <h1> leaves both headings
 * rendering the same words — the duplicate-heading state seen on
 * mcdermottremodeling's Central West End page. Picking an existing heading
 * instead promotes that element in place (h2 -> h1): nothing is added and the
 * wording is unchanged.
 */

import { useQuery } from "@tanstack/react-query";
import { ArrowUpToLine, Loader2, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { technicalAuditService } from "@/services/technicalAuditService";
import type { PageIssue, ProposedFix } from "@/types/technicalAudit";

interface H1CandidatePickerProps {
  issue: PageIssue;
  /** Same channel the diff cards use, so Apply picks the choice up unchanged. */
  onEdit: (value: ProposedFix) => void;
  /** Text currently staged for apply, so the active row can be highlighted. */
  selectedText?: string | null;
  /** Un-stages the promotion. With the selected row unclickable and the
   * generated editor below locked, this is the way back to the generated
   * heading without closing the drawer. */
  onClear?: () => void;
}

export function H1CandidatePicker({
  issue,
  onEdit,
  selectedText,
  onClear,
}: H1CandidatePickerProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["h1-promote-candidates", issue.id],
    queryFn: () => technicalAuditService.getH1PromoteCandidates(issue.id),
    // Reads the live page + builder data — too slow to refetch on every focus.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Looking for headings already on the page…
      </div>
    );
  }

  // A failed lookup must never block the normal generated-suggestion flow —
  // but it must not vanish silently either: an invisible panel is
  // indistinguishable from a broken one (which is exactly how the composite
  // issue-type gating bug presented). Errors stay quiet; an EMPTY result with
  // a known reason says so in one muted line.
  if (isError || !data) return null;
  if (data.candidates.length === 0) {
    if (!data.reason) return null;
    return (
      <p className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-[11px] text-muted-foreground">
        {data.reason}
      </p>
    );
  }

  return (
    <section className="rounded-xl border border-border bg-muted/40 p-4">
      <header className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <ArrowUpToLine className="h-4 w-4 text-muted-foreground" />
          Use a heading already on the page
        </h4>
        {data.builder && (
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
            {data.builder}
          </Badge>
        )}
      </header>

      <p className="mt-1.5 text-[11px] text-muted-foreground">
        Promotes the heading in place, so the page keeps its exact wording and
        nothing new is added.
      </p>

      <ul className="mt-3 space-y-1.5">
        {data.candidates.map((candidate) => {
          const active = selectedText === candidate.text;
          return (
            <li
              key={`${candidate.element_id ?? ""}:${candidate.text}`}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
                active ? "border-coral bg-coral-light" : "border-border bg-card"
              }`}
            >
              <Badge variant="outline" className="shrink-0 font-mono text-[10px] uppercase">
                {candidate.level}
              </Badge>
              <span className="flex-1 truncate text-[13px] text-foreground" title={candidate.text}>
                {candidate.text}
              </span>
              <Button
                size="sm"
                variant={active ? "default" : "outline"}
                aria-pressed={active}
                disabled={!data.promotable || active}
                onClick={() =>
                  onEdit({
                    promote_existing: {
                      text: candidate.text,
                      level: candidate.level,
                      element_id: candidate.element_id,
                    },
                  } as ProposedFix)
                }
              >
                {active ? "Selected" : "Use this"}
              </Button>
            </li>
          );
        })}
      </ul>

      {/* Why the buttons are disabled — without this the list looks broken. */}
      {!data.promotable && data.reason && (
        <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">{data.reason}</p>
      )}

      {selectedText ? (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground">
            Applying will promote the selected heading. The generated-heading
            editor below is disabled while this selection is staged.
          </p>
          {onClear && (
            <Button
              size="sm"
              variant="outline"
              onClick={onClear}
              className="h-7 shrink-0 px-2.5 text-[11px]"
            >
              Clear selection
            </Button>
          )}
        </div>
      ) : (
        data.promotable && (
          <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            Or use the generated heading under “Proposed Fix” below — that
            option adds a brand-new H1 to the page instead of promoting an
            existing heading.
          </p>
        )
      )}
    </section>
  );
}
